import { useGetLogs } from '@shadcn/hooks/use-logs';
import { getDetailedLogQuery, LogsTableName } from '@shadcn/lib/logs';
import { Alert } from '~/components/ui/Alert';
import { Skeleton } from '~/components/ui/Skeleton';
import { useMemo } from 'react';
import { IconButton } from '~/components/ui/IconButton';
import { Editor } from '@monaco-editor/react';
import { themeIsDark } from '~/lib/stores/theme';

export function LogView({ id, onBack, tableName }: { id: string; onBack: () => void; tableName: LogsTableName }) {
  const sql = useMemo(() => getDetailedLogQuery(id, tableName), [id]);
  const { data, isLoading, error } = useGetLogs({ sql });

  const formattedLog = data?.result[0] ? JSON.stringify(data?.result[0], null, 2) : '';

  return (
    <div className="pt-12">
      <div className="flex items-center gap-2 text-bolt-ds-textPrimary mb-6">
        <IconButton variant="link" icon="i-heroicons:arrow-left size-4 mb-0.5" onClick={onBack} size="sm" />
        <h2 className="text-lg font-semibold">Log {id} details</h2>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}
      {!isLoading && (error || !formattedLog) && (
        <Alert type="danger" title="Error fetching log">
          {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
        </Alert>
      )}
      {!isLoading && formattedLog && (
        <div className="rounded-lg border border-bolt-ds-borderOutline overflow-hidden">
          <Editor
            height="555px"
            language="json"
            value={formattedLog}
            theme={themeIsDark() ? 'vs-dark' : 'vs'}
            className="bg-transparent"
            options={{
              minimap: { enabled: false },
              lineNumbers: 'off',
              language: 'json',
              renderLineHighlight: 'none',
              selectionHighlight: false,
              fontSize: 16,
              readOnly: true,
              guides: { indentation: false },
              padding: {
                top: 10,
                bottom: 24,
              },
            }}
          />
        </div>
      )}
    </div>
  );
}
